import { ButtonsContainer, Modal, PrimaryButton, SecondaryButton, UUID } from '@cmg/common';
import { SnackbarManager } from '@cmg/design-system';
import { AgGridEvent } from 'ag-grid-community';
import React, { useRef } from 'react';
import { connectModal, hide, InjectedProps, show } from 'redux-modal';

import Loading from '../../../../../../common/components/indicators/loading/Loading';
import ServerErrorsBanner from '../../../../../../common/components/indicators/server-error/ServerErrorsBanner';
import { useGridReady } from '../../../../../../common/hooks/useGridReady';
import { updateDemandGridRowsReactiveVar } from '../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { useFirmKeyOrIdContext } from '../../../../contexts/FirmKeyOrIdContext';
import {
  OrderBookPersonaType,
  useIdentifyOrderBookPersona,
} from '../../../hooks/useIdentifyOrderBookPersona';
import CrmSelectGrid from './components/CrmSelectGrid';
import { CrmSelectModalHeader } from './components/CrmSelectModalHeader';
import { CrmSelectionRow, useGetCrmModel } from './CrmSelectModal.model';
import { SFooterContainer, SModalBodyContainer, STitleContainer } from './CrmSelectModal.styles';
import { useCrmSelection_AssignInvestorKeyMutation } from './graphql/__generated__';
import { useAssignCoveredInvestorKeyMutation } from './hooks/useCoveredAssignInvestorKeyMutation';

type OwnProps = {
  offeringId?: UUID;
  indicationId?: UUID;
  cmgEntityKey?: string;
  cmgEntityName?: string | null;
  firmKey?: string | null;
  duplicateIndicationIds?: readonly string[];
};

export type Props = OwnProps & InjectedProps;

export const CrmSelectModal: React.FC<Props> = ({
  handleHide,
  offeringId,
  indicationId,
  cmgEntityKey,
  firmKey,
  duplicateIndicationIds,
}) => {
  const { type: personaType } = useIdentifyOrderBookPersona({ offeringId });
  const isSyndicate = personaType === OrderBookPersonaType.SYNDICATE;
  const isSalesAndTrading = personaType === OrderBookPersonaType.SALES_AND_TRADING;

  const {
    data: rows,
    loading: loadingCrmModel,
    error: getCrmError,
  } = useGetCrmModel({ cmgEntityKey, offeringId, indicationId, duplicateIndicationIds });

  const gridRef = useRef<AgGridEvent | undefined>();
  const onGridReady = useGridReady(gridRef);
  const { setFirmKeyOrId } = useFirmKeyOrIdContext();
  const [assignInvestorKey, { loading: assignInvestorKeyLoading, error: assignInvestorKeyError }] =
    useCrmSelection_AssignInvestorKeyMutation();

  const [
    assignCoveredInvestorKey,
    { loading: assignCoveredInvestorKeyLoading, error: assignCoveredInvestorKeyError },
  ] = useAssignCoveredInvestorKeyMutation();

  const onSave = async () => {
    const selectedRows = (gridRef.current?.api.getSelectedRows() as CrmSelectionRow[]) ?? [];
    const selectedRow = selectedRows.length > 0 ? selectedRows[0] : null;
    if (!selectedRow || !indicationId || !offeringId || !(isSyndicate || isSalesAndTrading)) {
      return;
    }

    if (isSyndicate) {
      await assignInvestorKey({
        variables: {
          offeringId: offeringId,
          indicationId: indicationId,
          investorCrmKey: selectedRow.id,
        },
      });

      updateDemandGridRowsReactiveVar({ fetchIds: [indicationId] });
    }

    if (isSalesAndTrading) {
      await assignCoveredInvestorKey({
        variables: {
          offeringId: offeringId,
          indicationId: indicationId,
          investorCrmKey: selectedRow.id,
        },
      });
    }

    setFirmKeyOrId(selectedRow.id);
    SnackbarManager.success('Successfully selected investor key');
    handleHide();
  };

  const loading = loadingCrmModel || assignInvestorKeyLoading || assignCoveredInvestorKeyLoading;
  const error = getCrmError ?? assignInvestorKeyError ?? assignCoveredInvestorKeyError;

  return (
    <Modal
      show
      title={<STitleContainer>Select CRM Bank Investor Key</STitleContainer>}
      onHide={handleHide}
      closeButton
      shouldCloseOnOverlayClick
      size="large"
      footer={
        <SFooterContainer>
          <ButtonsContainer justifyContent="right" margin={16}>
            <SecondaryButton onClick={handleHide}>Close</SecondaryButton>
            <PrimaryButton onClick={onSave} disabled={loading}>
              Save
            </PrimaryButton>
          </ButtonsContainer>
        </SFooterContainer>
      }
    >
      <SModalBodyContainer>
        <CrmSelectModalHeader />
        {error && <ServerErrorsBanner error={error!} />}
        {loading && <Loading />}
        {!loading && (
          <CrmSelectGrid
            data-testid="crm-select-grid"
            isLoading={loadingCrmModel}
            firmKey={firmKey}
            rows={rows}
            onGridReady={onGridReady}
            isSyndicate={isSyndicate}
          />
        )}
      </SModalBodyContainer>
    </Modal>
  );
};

const name = 'ORDERBOOK/CRM_SELECTION_SIDEPANEL_MODAL';

export const openCrmSelectionModal = (props: OwnProps) => show(name, props);
export const closeCrmSelectionModal = () => hide(name);

export default connectModal({ name })(CrmSelectModal) as React.ComponentClass<OwnProps>;
